import { CheckCircle2, ChevronDown, MapPin } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  formatPrice,
  STORE_CONTACT_SUPPORTED_COUNTRIES,
  useLocalities,
  useTranslation,
} from '@dukanti/shared';
import type {
  FulfillmentSelection,
  PublicFulfillmentOptions,
  Locality,
  TranslationKey,
} from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { PreparedOrderDeliveryDraft } from '../lib/admin-prepared-order-schema';
import { normalizePreparedOrderPhone } from '../lib/admin-prepared-order-schema';

export interface AdminPreparedOrderDeliveryFormProps {
  readonly value: PreparedOrderDeliveryDraft;
  readonly onChange: (value: PreparedOrderDeliveryDraft) => void;
  readonly showValidation?: boolean;
  readonly fulfillment: FulfillmentSelection | null;
  readonly options: PublicFulfillmentOptions | null;
  readonly onFulfillmentChange: (selection: FulfillmentSelection) => void;
}

type DeliveryField = 'phoneNumber' | 'localityId';

function localityName(locality: Locality, lang: 'ar' | 'he' | 'en'): string {
  if (lang === 'ar') return locality.name_ar ?? locality.name_he ?? locality.name_en ?? '';
  if (lang === 'he') return locality.name_he ?? locality.name_ar ?? locality.name_en ?? '';
  return locality.name_en ?? locality.name_ar ?? locality.name_he ?? '';
}

function filterLocalities(
  localities: readonly Locality[],
  query: string,
  lang: 'ar' | 'he' | 'en',
): Locality[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return [];

  return localities
    .filter((locality) => {
      return [locality.name_ar, locality.name_he, locality.name_en, String(locality.symbol_number)]
        .filter((value): value is string => Boolean(value))
        .some((value) => value.toLowerCase().includes(trimmed));
    })
    .sort((a, b) => localityName(a, lang).localeCompare(localityName(b, lang)))
    .slice(0, 8);
}

function normalizeLocalitySearchValue(value: string): string {
  return value.trim().toLowerCase();
}

function findExactLocality(localities: readonly Locality[], query: string): Locality | undefined {
  const normalizedQuery = normalizeLocalitySearchValue(query);
  if (!normalizedQuery) return undefined;

  return localities.find((locality) =>
    [locality.name_ar, locality.name_he, locality.name_en, String(locality.symbol_number)]
      .filter((value): value is string => Boolean(value))
      .some((value) => normalizeLocalitySearchValue(value) === normalizedQuery),
  );
}

function getDeliveryFieldErrors(
  value: PreparedOrderDeliveryDraft,
  localities: readonly Locality[] | undefined,
): Partial<Record<DeliveryField, TranslationKey>> {
  const errors: Partial<Record<DeliveryField, TranslationKey>> = {};

  if (value.phoneNumber.trim() === '') {
    errors.phoneNumber = 'admin.orders.prepare.errors.phoneRequired' as TranslationKey;
  } else {
    const phone = normalizePreparedOrderPhone(value);
    if (!phone.ok) errors.phoneNumber = phone.error as TranslationKey;
  }

  if (value.localityId.trim() === '') {
    errors.localityId = 'admin.orders.prepare.errors.localityRequired' as TranslationKey;
  } else if (localities && !localities.some((candidate) => candidate.id === value.localityId)) {
    errors.localityId = 'admin.orders.prepare.errors.localityInvalid' as TranslationKey;
  }

  return errors;
}

export function AdminPreparedOrderDeliveryForm(
  props: AdminPreparedOrderDeliveryFormProps,
): React.JSX.Element {
  const { t, lang } = useTranslation();
  const localities = useLocalities(supabase);
  const [localitySearch, setLocalitySearch] = useState('');
  const [localityResultsOpen, setLocalityResultsOpen] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Partial<Record<DeliveryField, boolean>>>({});
  const selectedLocality = (localities.data ?? []).find(
    (locality) => locality.id === props.value.localityId,
  );
  useEffect(() => {
    if (selectedLocality) {
      setLocalitySearch(localityName(selectedLocality, lang));
      setLocalityResultsOpen(false);
    }
  }, [lang, selectedLocality]);
  const shouldShowLocalityResults =
    localityResultsOpen && !props.value.localityId && localitySearch.trim().length > 0;
  const visibleLocalities = shouldShowLocalityResults
    ? filterLocalities(localities.data ?? [], localitySearch, lang)
    : [];
  const fieldErrors = getDeliveryFieldErrors(props.value, localities.data);
  if (props.fulfillment?.method === 'pickup') delete fieldErrors.localityId;
  const shouldShowError = (field: DeliveryField): boolean =>
    Boolean(fieldErrors[field] && (props.showValidation || touchedFields[field]));
  const markTouched = (field: DeliveryField): void => {
    setTouchedFields((current) => ({ ...current, [field]: true }));
  };
  const phoneNumberErrorId = 'prepared-order-phone-number-error';
  const localityErrorId = 'prepared-order-locality-error';
  const localityListboxId = 'prepared-order-locality-listbox';

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <h2 className="font-display text-foreground mb-4 text-xl font-semibold">
        {t('fulfillment.title')}
      </h2>
      <fieldset className="mb-5 space-y-3">
        <legend className="mb-2 font-medium">{t('fulfillment.chooseMethod')}</legend>
        {props.options?.delivery ? (
          <label className="border-border flex cursor-pointer items-center gap-3 rounded-xl border p-4">
            <input
              type="radio"
              name="prepared-fulfillment"
              checked={props.fulfillment?.method === 'delivery'}
              onChange={() =>
                props.onFulfillmentChange({ method: 'delivery', pickupPlaceId: null })
              }
            />
            <span>
              <span className="block font-medium">{t('fulfillment.delivery')}</span>
              <span className="text-muted-foreground block text-sm">
                {props.options.delivery.feeCents === 0
                  ? t('fulfillment.freeDelivery')
                  : formatPrice(props.options.delivery.feeCents, lang)}
              </span>
            </span>
          </label>
        ) : null}
        {props.options?.pickupPlaces.map((place) => (
          <label
            key={place.id}
            className="border-border flex cursor-pointer items-start gap-3 rounded-xl border p-4"
          >
            <input
              type="radio"
              name="prepared-fulfillment"
              checked={
                props.fulfillment?.method === 'pickup' &&
                props.fulfillment.pickupPlaceId === place.id
              }
              onChange={() =>
                props.onFulfillmentChange({ method: 'pickup', pickupPlaceId: place.id })
              }
            />
            <span className="min-w-0">
              <span className="block font-medium">{t('fulfillment.freePickup')}</span>
              <bdi className="block break-words">{place.name}</bdi>
              <bdi className="text-muted-foreground block whitespace-pre-wrap break-words">
                {place.details}
              </bdi>
            </span>
          </label>
        ))}
        {!props.options || (!props.options.delivery && props.options.pickupPlaces.length === 0) ? (
          <p role="status" className="text-muted-foreground">
            {t('fulfillment.unavailable')}
          </p>
        ) : null}
      </fieldset>
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-muted-foreground text-sm">
          {t('admin.orders.prepare.delivery.customerNameOptional' as TranslationKey)}
          <Input
            value={props.value.customerName}
            onChange={(event) =>
              props.onChange({ ...props.value, customerName: event.target.value })
            }
            maxLength={200}
            placeholder={t(
              'admin.orders.prepare.delivery.customerNamePlaceholder' as TranslationKey,
            )}
            className="mt-1"
          />
        </label>
        <div className="grid grid-cols-[130px_1fr] gap-2">
          <label className="text-muted-foreground text-sm">
            {t('admin.orders.prepare.delivery.phoneCountry' as TranslationKey)}
            <div className="relative mt-1">
              <select
                value={props.value.phoneCountry}
                onChange={(event) =>
                  props.onChange({
                    ...props.value,
                    phoneCountry: event.target.value as PreparedOrderDeliveryDraft['phoneCountry'],
                  })
                }
                className="border-input bg-card text-foreground ring-offset-background hover:border-admin-lime hover:bg-admin-surface-muted focus-visible:border-admin-lime focus-visible:ring-admin-lime/20 h-10 w-full cursor-pointer appearance-none rounded-md border bg-none px-3 pe-10 text-start text-sm font-medium shadow-sm transition-colors [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {STORE_CONTACT_SUPPORTED_COUNTRIES.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.dialCode || country.code}
                  </option>
                ))}
              </select>
              <ChevronDown className="text-foreground/70 pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            </div>
          </label>
          <label className="text-muted-foreground text-sm">
            {t('admin.orders.prepare.delivery.phoneNumber' as TranslationKey)}
            <Input
              id="prepared-order-phone-number"
              value={props.value.phoneNumber}
              onChange={(event) =>
                props.onChange({ ...props.value, phoneNumber: event.target.value })
              }
              onBlur={() => markTouched('phoneNumber')}
              maxLength={32}
              placeholder={t('admin.orders.prepare.delivery.phonePlaceholder' as TranslationKey)}
              aria-invalid={shouldShowError('phoneNumber') ? true : undefined}
              aria-describedby={shouldShowError('phoneNumber') ? phoneNumberErrorId : undefined}
              className="mt-1"
            />
            {shouldShowError('phoneNumber') ? (
              <span
                id={phoneNumberErrorId}
                className="text-destructive mt-1 block text-xs"
                role="alert"
              >
                {t(fieldErrors.phoneNumber!)}
              </span>
            ) : null}
          </label>
        </div>
        {props.fulfillment?.method === 'delivery' ? (
          <>
            <div className="text-muted-foreground relative text-sm md:col-span-2">
              <label htmlFor="prepared-order-locality-search">
                {t('admin.orders.prepare.delivery.locality' as TranslationKey)}
              </label>
              <div className="relative mt-1">
                <MapPin className="text-muted-foreground absolute start-3 top-3 h-4 w-4" />
                <Input
                  id="prepared-order-locality-search"
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={shouldShowLocalityResults}
                  aria-controls={shouldShowLocalityResults ? localityListboxId : undefined}
                  value={localitySearch}
                  onChange={(event) => {
                    const nextSearch = event.target.value;
                    const exactLocality = findExactLocality(localities.data ?? [], nextSearch);
                    setLocalitySearch(nextSearch);
                    setLocalityResultsOpen(!exactLocality);
                    props.onChange({ ...props.value, localityId: exactLocality?.id ?? '' });
                  }}
                  onFocus={() => {
                    if (!props.value.localityId && localitySearch.trim().length > 0) {
                      setLocalityResultsOpen(true);
                    }
                  }}
                  onBlur={() => markTouched('localityId')}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setLocalityResultsOpen(false);
                  }}
                  placeholder={t(
                    'admin.orders.prepare.delivery.localityPlaceholder' as TranslationKey,
                  )}
                  aria-invalid={shouldShowError('localityId') ? true : undefined}
                  aria-describedby={shouldShowError('localityId') ? localityErrorId : undefined}
                  className="pe-10 ps-10"
                />
                {selectedLocality ? (
                  <CheckCircle2 className="text-admin-lime absolute end-3 top-3 h-4 w-4" />
                ) : (
                  <ChevronDown className="text-muted-foreground pointer-events-none absolute end-3 top-3 h-4 w-4" />
                )}
              </div>
              {shouldShowError('localityId') ? (
                <span
                  id={localityErrorId}
                  className="text-destructive mt-1 block text-xs"
                  role="alert"
                >
                  {t(fieldErrors.localityId!)}
                </span>
              ) : null}
              {visibleLocalities.length > 0 ? (
                <div
                  id={localityListboxId}
                  role="listbox"
                  className="border-border bg-card shadow-admin-card absolute z-20 mt-1 grid max-h-56 w-full gap-1 overflow-auto rounded-lg border p-2"
                >
                  {visibleLocalities.map((locality) => (
                    <button
                      key={locality.id}
                      type="button"
                      role="option"
                      aria-selected={props.value.localityId === locality.id}
                      className="text-foreground hover:bg-muted rounded-md px-3 py-2 text-start text-sm"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setLocalitySearch(localityName(locality, lang));
                        setLocalityResultsOpen(false);
                        props.onChange({ ...props.value, localityId: locality.id });
                      }}
                    >
                      {localityName(locality, lang)}
                    </button>
                  ))}
                </div>
              ) : shouldShowLocalityResults ? (
                <p className="border-border bg-card shadow-admin-card absolute z-20 mt-1 w-full rounded-lg border p-3 text-sm">
                  {t('admin.orders.prepare.delivery.localityEmpty' as TranslationKey)}
                </p>
              ) : null}
            </div>
            <label className="text-muted-foreground text-sm md:col-span-2">
              {t('admin.orders.prepare.delivery.addressLine1Optional' as TranslationKey)}
              <Input
                value={props.value.addressLine1}
                onChange={(event) =>
                  props.onChange({ ...props.value, addressLine1: event.target.value })
                }
                maxLength={300}
                placeholder={t(
                  'admin.orders.prepare.delivery.addressLine1Placeholder' as TranslationKey,
                )}
                className="mt-1"
              />
            </label>
          </>
        ) : null}
        <label className="text-muted-foreground text-sm md:col-span-2">
          {t('admin.orders.prepare.delivery.notes' as TranslationKey)}
          <Textarea
            value={props.value.customerNotes}
            onChange={(event) =>
              props.onChange({ ...props.value, customerNotes: event.target.value })
            }
            maxLength={500}
            placeholder={t('admin.orders.prepare.delivery.notesPlaceholder' as TranslationKey)}
            className="mt-1"
          />
        </label>
      </div>
    </section>
  );
}
